import { RecruitmentStatus } from "@/lib/schema";
import { getRecruitmentStatusConfig, RECRUITMENT_STATUS_ICONS } from "@/lib/recruitmentStatusCopy";

type IndicatorSize = "sm" | "md" | "lg";

interface RecruitmentStatusIndicatorProps {
  /** The recruitment status to display */
  status?: RecruitmentStatus;
  /** Size variant */
  size?: IndicatorSize;
  /** Show as callout box with description (for job details page) */
  showCallout?: boolean;
  /** Additional className */
  className?: string;
}

const SIZE_CONFIG = {
  sm: {
    container: "w-6 h-6",
    icon: "w-3 h-3",
    text: "text-xs",
    gap: "gap-1.5",
  },
  md: {
    container: "w-8 h-8",
    icon: "w-4 h-4",
    text: "text-sm",
    gap: "gap-2",
  },
  lg: {
    container: "w-10 h-10",
    icon: "w-5 h-5",
    text: "text-base",
    gap: "gap-2.5",
  },
};

export function RecruitmentStatusIndicator({
  status,
  size = "md",
  showCallout = false,
  className = "",
}: RecruitmentStatusIndicatorProps) {
  const config = getRecruitmentStatusConfig(status);
  const IconComponent = RECRUITMENT_STATUS_ICONS[config.icon];
  const sizeConfig = SIZE_CONFIG[size];

  // Basic indicator with icon and label
  const indicator = (
    <div className={`flex items-center ${sizeConfig.gap} ${className}`}>
      {/* Icon Badge */}
      <div
        className={`flex items-center justify-center ${sizeConfig.container} rounded-full flex-shrink-0`}
        style={{ backgroundColor: config.bgColor }}
      >
        <IconComponent className={`${sizeConfig.icon} text-white`} />
      </div>
      
      {/* Status Label */}
      <span
        className={`font-bold ${sizeConfig.text} uppercase space-grotesk tracking-wide`}
        style={{ color: config.color }}
      >
        {config.label}
      </span>
    </div>
  );

  // If callout mode, wrap with description box
  if (showCallout) {
    return (
      <div className="mb-6">
        {indicator}
        <div
          className="mt-2 p-4 rounded-lg border"
          style={{
            backgroundColor: config.lightBgColor,
            borderColor: config.borderColor,
          }}
        >
          <h4
            className="text-sm font-semibold space-grotesk mb-1"
            style={{ color: config.color }}
          >
            {config.calloutTitle}
          </h4>
          <p className="text-sm text-slate-700 font-manrope">
            {config.calloutDescription}
          </p>
        </div>
      </div>
    );
  }

  return indicator;
}

